import { db } from '../config/firebase';
import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, Timestamp, onSnapshot, writeBatch
} from 'firebase/firestore';
import { Project, ProjectStats, ProjectUser, SchoolSegment, SegmentStats } from '../types/project.types';
import { UserRole } from '../types/user.types';

export const projectService = {
  async createProject(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const projectRef = doc(collection(db, 'projects'));
    const projectData = {
      ...project,
      id: projectRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    await setDoc(projectRef, projectData);
    
    for (const user of project.assignedUsers) {
      await this.addUserToProject(user.userId, projectRef.id);
    }
    
    return projectRef.id;
  },

  async updateProject(projectId: string, updates: any): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, updates);
  },
  

    async getProject(projectId: string): Promise<Project | null> {
     const projectDoc = await getDoc(doc(db, 'projects', projectId));
     if (projectDoc.exists()) {
    return projectDoc.data() as Project;
  }
  return null;
},


async getUserProjects(userId: string): Promise<Project[]> {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    return [];
  }

  const userData = userDoc.data();

  const userProjects = userData.projects || [];

  if (userProjects.length === 0) {
    return [];
  }

  const projectPromises = userProjects.map((projectId: string) =>
    this.getProject(projectId)
  );

  const projects = await Promise.all(projectPromises);
  const validProjects = projects.filter((p): p is Project => p !== null);

  return validProjects;
},

async addUserToProject(userId: string, projectId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentProjects = userData.projects || [];
      
      if (!currentProjects.includes(projectId)) {
        await updateDoc(userRef, {
          projects: [...currentProjects, projectId],
          updatedAt: Timestamp.now()
        });
      }
    } else {
      console.error('User profile not found:', userId);
    }
  } catch (error) {
    console.error('Error adding user to project:', error);
    // Don't throw - allow project creation to continue
  }
},

  async getProjectStats(projectId: string): Promise<ProjectStats> {
    const leadsQuery = query(
      collection(db, 'leads'),
      where('projectId', '==', projectId)
    );
    const snapshot = await getDocs(leadsQuery);
    const leads = snapshot.docs.map(doc => doc.data());

    let totalChildren = 0;
    let validChildren = 0;
    let invalidChildren = 0;
    let pendingChildren = 0;
    let enrolledChildren = 0;

    // Per-school-segment breakdown. Seeded from the project's segments so that
    // schools with zero leads still appear in the result.
    const projectForSegments = await this.getProject(projectId);
    const segments: SchoolSegment[] = projectForSegments?.schoolSegments || [];
    const bySegment: { [segmentId: string]: SegmentStats } = {};
    const segBucket = (segmentId?: string): SegmentStats | null => {
      if (!segmentId) return null;
      if (!bySegment[segmentId]) {
        bySegment[segmentId] = {
          totalLeads: 0, validLeads: 0, invalidLeads: 0,
          pendingValidation: 0, enrollments: 0
        };
      }
      return bySegment[segmentId];
    };
    segments.forEach(s => segBucket(s.id));

    leads.forEach(lead => {
      const children = lead.children || [];
      const leadIsValid = lead.determination === 'valid';
      const leadIsInvalid = lead.determination === 'invalid';
      const leadIsPending = lead.determination === 'pending';

      const tally = (bucket: SegmentStats | null, isEnrolled: boolean) => {
        if (!bucket) return;
        bucket.totalLeads++;
        if (leadIsValid) bucket.validLeads++;
        else if (leadIsInvalid) bucket.invalidLeads++;
        else if (leadIsPending) bucket.pendingValidation++;
        if (isEnrolled) bucket.enrollments++;
      };

      children.forEach((child: any) => {
        totalChildren++;

        // Child inherits lead's determination status
        if (leadIsValid) {
          validChildren++;
        } else if (leadIsInvalid) {
          invalidChildren++;
        } else if (leadIsPending) {
          pendingChildren++;
        }

        // Track individual child enrollment using LeadStatus
        // Child status can be: uncontacted, cold, warm, hot, enrolled, lost, dnc
        if (child.status === 'enrolled') {
          enrolledChildren++;
        }

        // Per-child campus override falls back to the lead's segment.
        tally(segBucket(child.schoolSegmentId || lead.schoolSegmentId), child.status === 'enrolled');
      });

      // If lead has no children, count the lead itself as one unit
      if (children.length === 0) {
        totalChildren++;
        if (leadIsValid) validChildren++;
        else if (leadIsInvalid) invalidChildren++;
        else if (leadIsPending) pendingChildren++;
        if (lead.status === 'enrolled') enrolledChildren++;
        tally(segBucket(lead.schoolSegmentId), lead.status === 'enrolled');
      }
    });

    // Get workloads for this project
    const workloadsQuery = query(
      collection(db, 'workloads'),
      where('projectId', '==', projectId)
    );
    const workloadsSnapshot = await getDocs(workloadsQuery);

    let canvassingStops = 0;

    for (const workloadDoc of workloadsSnapshot.docs) {
      try {
        const addressesRef = collection(db, 'workloads', workloadDoc.id, 'addresses');
        const addressesSnapshot = await getDocs(addressesRef);

        addressesSnapshot.docs.forEach(addrDoc => {
          const addr = addrDoc.data();
          if (addr.status && addr.status !== 'pending') {
            canvassingStops++;
          }
        });
      } catch (error) {
        console.error(`Error fetching addresses for workload ${workloadDoc.id}:`, error);
      }
    }

    const surveyResponsesQuery = query(
      collection(db, 'surveyResponses'),
      where('projectId', '==', projectId)
    );
    const surveyResponsesSnapshot = await getDocs(surveyResponsesQuery);

    let surveysCompleted = 0;
    surveyResponsesSnapshot.docs.forEach(doc => {
      const response = doc.data();
      if (!response.discontinued) {
        surveysCompleted++;
      }
    });

    // Calculate survey rate
    const surveyRate = canvassingStops > 0 ? (surveysCompleted / canvassingStops) * 100 : 0;

    const stats: ProjectStats = {
      totalLeads: totalChildren, // Now counts children
      validLeads: validChildren,
      invalidLeads: invalidChildren,
      pendingValidation: pendingChildren,
      enrollments: enrolledChildren, // Counts enrolled children
      conversionRate: 0,
      canvassingStops,
      surveysCompleted,
      surveyRate,
      lastUpdated: Timestamp.now(),
      ...(segments.length > 0 ? { bySegment } : {})
    };

    if (stats.validLeads > 0) {
      stats.conversionRate = (stats.enrollments / stats.validLeads) * 100;
    }

    return stats;
  },

  // ---- School segment management -------------------------------------------
  // Segments are stored as an array on the project document. These helpers keep
  // the array consistent and generate stable IDs.

  async addSchoolSegment(projectId: string, segment: Omit<SchoolSegment, 'id' | 'createdAt'>): Promise<string> {
    const project = await this.getProject(projectId);
    if (!project) throw new Error('Project not found');
    const segmentId = doc(collection(db, 'projects')).id; // reuse Firestore ID generator
    const newSegment: SchoolSegment = {
      ...segment,
      id: segmentId,
      createdAt: Timestamp.now()
    };
    const segments = [...(project.schoolSegments || []), newSegment];
    await updateDoc(doc(db, 'projects', projectId), {
      schoolSegments: segments,
      updatedAt: Timestamp.now()
    });
    return segmentId;
  },

  async updateSchoolSegment(projectId: string, segmentId: string, updates: Partial<Omit<SchoolSegment, 'id'>>): Promise<void> {
    const project = await this.getProject(projectId);
    if (!project) throw new Error('Project not found');
    const segments = (project.schoolSegments || []).map(s =>
      s.id === segmentId ? { ...s, ...updates } : s
    );
    await updateDoc(doc(db, 'projects', projectId), {
      schoolSegments: segments,
      updatedAt: Timestamp.now()
    });
  },

  async removeSchoolSegment(projectId: string, segmentId: string): Promise<void> {
    const project = await this.getProject(projectId);
    if (!project) throw new Error('Project not found');
    const segments = (project.schoolSegments || []).filter(s => s.id !== segmentId);
    await updateDoc(doc(db, 'projects', projectId), {
      schoolSegments: segments,
      updatedAt: Timestamp.now()
    });
  },

    subscribeToProjects(userId: string, callback: (projects: Project[]) => void) {
  const userRef = doc(db, 'users', userId);

  return onSnapshot(userRef, async (userDoc) => {
    if (!userDoc.exists()) {
      callback([]);
      return;
    }

    const userData = userDoc.data();

    const projectIds = userData.projects || [];

    if (projectIds.length === 0) {
      callback([]);
      return;
    }

    // Fetch all projects
    const projectPromises = projectIds.map((projectId: string) =>
      this.getProject(projectId)
    );

    const projects = await Promise.all(projectPromises);
    const validProjects = projects.filter((p): p is Project => p !== null);

    callback(validProjects);
  });
},

  async getOrganizationProjects(organization: string): Promise<Project[]> {
    const q = query(collection(db, 'projects'), where('organizationId', '==', organization));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
  },

  async getAllProjects(): Promise<Project[]> {
    const snapshot = await getDocs(collection(db, 'projects'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
  },

  async removeUserFromProject(userId: string, projectId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentProjects = userData.projects || [];
        const updatedProjects = currentProjects.filter((id: string) => id !== projectId);

        await updateDoc(userRef, {
          projects: updatedProjects,
          updatedAt: Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error removing user from project:', error);
    }
  },

  async archiveProject(projectId: string): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      status: 'archived',
      archivedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  },

  async unarchiveProject(projectId: string): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      status: 'active',
      archivedAt: null,
      updatedAt: Timestamp.now()
    });
  },

  async getArchivedProjects(organizationId: string): Promise<Project[]> {
    const q = query(
      collection(db, 'projects'),
      where('organizationId', '==', organizationId),
      where('status', '==', 'archived')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
  },

  async getAllProjectsForStats(organizationId: string): Promise<Project[]> {
    const q = query(
      collection(db, 'projects'),
      where('organizationId', '==', organizationId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
  },

  async getOrganizationUsers(orgId: string): Promise<{ uid: string; name: string; email: string; role: UserRole; projects: string[] }[]> {
    const q = query(collection(db, 'users'), where('organizationId', '==', orgId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return {
        uid: data.uid || d.id,
        name: data.name || '',
        email: data.email || '',
        role: data.role as UserRole,
        projects: data.projects || []
      };
    });
  },

  async syncAssignedUsers(
    projectId: string,
    newAssignedUsers: ProjectUser[],
    oldAssignedUsers: ProjectUser[]
  ): Promise<void> {
    const batch = writeBatch(db);

    // Update project.assignedUsers
    const projectRef = doc(db, 'projects', projectId);
    batch.update(projectRef, {
      assignedUsers: newAssignedUsers,
      updatedAt: Timestamp.now()
    });

    const oldIds = new Set(oldAssignedUsers.map(u => u.userId));
    const newIds = new Set(newAssignedUsers.map(u => u.userId));

    // Users added to project
    const addedUserIds = newAssignedUsers
      .filter(u => !oldIds.has(u.userId))
      .map(u => u.userId);

    // Users removed from project
    const removedUserIds = oldAssignedUsers
      .filter(u => !newIds.has(u.userId))
      .map(u => u.userId);

    // Add projectId to newly added users' projects[]
    for (const userId of addedUserIds) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentProjects: string[] = userData.projects || [];
        if (!currentProjects.includes(projectId)) {
          batch.update(userRef, {
            projects: [...currentProjects, projectId],
            updatedAt: Timestamp.now()
          });
        }
      }
    }

    // Remove projectId from removed users' projects[]
    for (const userId of removedUserIds) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentProjects: string[] = userData.projects || [];
        batch.update(userRef, {
          projects: currentProjects.filter((id: string) => id !== projectId),
          updatedAt: Timestamp.now()
        });
      }
    }

    await batch.commit();
  },

  async syncUserProjectAssignments(
    userId: string,
    role: UserRole,
    oldProjectIds: string[],
    newProjectIds: string[]
  ): Promise<void> {
    const oldSet = new Set(oldProjectIds);
    const newSet = new Set(newProjectIds);

    const addedProjects = newProjectIds.filter(id => !oldSet.has(id));
    const removedProjects = oldProjectIds.filter(id => !newSet.has(id));

    // Add user to newly assigned projects' assignedUsers[]
    for (const projectId of addedProjects) {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const assignedUsers: ProjectUser[] = projectData.assignedUsers || [];
        const alreadyAssigned = assignedUsers.some(u => u.userId === userId);
        if (!alreadyAssigned) {
          assignedUsers.push({
            userId,
            role,
            permissions: [],
            assignedAt: Timestamp.now()
          });
          await updateDoc(projectRef, {
            assignedUsers,
            updatedAt: Timestamp.now()
          });
        }
      }
    }

    // Remove user from removed projects' assignedUsers[]
    for (const projectId of removedProjects) {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const assignedUsers: ProjectUser[] = projectData.assignedUsers || [];
        const filtered = assignedUsers.filter(u => u.userId !== userId);
        await updateDoc(projectRef, {
          assignedUsers: filtered,
          updatedAt: Timestamp.now()
        });
      }
    }
  },

  async deleteProject(projectId: string): Promise<{ success: boolean; deletedCounts: { leads: number; workloads: number; addresses: number; surveyResponses: number } }> {
    const deletedCounts = {
      leads: 0,
      workloads: 0,
      addresses: 0,
      surveyResponses: 0
    };

    try {
      // 1. Get the project to find assigned users
      const project = await this.getProject(projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      // 2. Delete all leads for this project
      const leadsQuery = query(collection(db, 'leads'), where('projectId', '==', projectId));
      const leadsSnapshot = await getDocs(leadsQuery);

      // Use batched writes for efficiency (max 500 per batch)
      const leadBatches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;

      for (const leadDoc of leadsSnapshot.docs) {
        currentBatch.delete(leadDoc.ref);
        operationCount++;
        deletedCounts.leads++;

        if (operationCount >= 450) {
          leadBatches.push(currentBatch);
          currentBatch = writeBatch(db);
          operationCount = 0;
        }
      }
      if (operationCount > 0) {
        leadBatches.push(currentBatch);
      }

      // Commit lead deletion batches
      for (const batch of leadBatches) {
        await batch.commit();
      }

      // 3. Delete all workloads and their addresses subcollections
      const workloadsQuery = query(collection(db, 'workloads'), where('projectId', '==', projectId));
      const workloadsSnapshot = await getDocs(workloadsQuery);

      for (const workloadDoc of workloadsSnapshot.docs) {
        // Delete addresses subcollection
        const addressesRef = collection(db, 'workloads', workloadDoc.id, 'addresses');
        const addressesSnapshot = await getDocs(addressesRef);

        const addressBatches: ReturnType<typeof writeBatch>[] = [];
        let addrBatch = writeBatch(db);
        let addrCount = 0;

        for (const addressDoc of addressesSnapshot.docs) {
          addrBatch.delete(addressDoc.ref);
          addrCount++;
          deletedCounts.addresses++;

          if (addrCount >= 450) {
            addressBatches.push(addrBatch);
            addrBatch = writeBatch(db);
            addrCount = 0;
          }
        }
        if (addrCount > 0) {
          addressBatches.push(addrBatch);
        }

        for (const batch of addressBatches) {
          await batch.commit();
        }

        // Delete the workload document
        await deleteDoc(workloadDoc.ref);
        deletedCounts.workloads++;
      }

      // 4. Delete all survey responses for this project
      const surveyResponsesQuery = query(collection(db, 'surveyResponses'), where('projectId', '==', projectId));
      const surveyResponsesSnapshot = await getDocs(surveyResponsesQuery);

      const responseBatches: ReturnType<typeof writeBatch>[] = [];
      let respBatch = writeBatch(db);
      let respCount = 0;

      for (const responseDoc of surveyResponsesSnapshot.docs) {
        respBatch.delete(responseDoc.ref);
        respCount++;
        deletedCounts.surveyResponses++;

        if (respCount >= 450) {
          responseBatches.push(respBatch);
          respBatch = writeBatch(db);
          respCount = 0;
        }
      }
      if (respCount > 0) {
        responseBatches.push(respBatch);
      }

      for (const batch of responseBatches) {
        await batch.commit();
      }

      // 5. Remove project from all assigned users
      if (project.assignedUsers && project.assignedUsers.length > 0) {
        for (const user of project.assignedUsers) {
          await this.removeUserFromProject(user.userId, projectId);
        }
      }

      // 6. Delete the project document itself
      await deleteDoc(doc(db, 'projects', projectId));

      return { success: true, deletedCounts };
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }
};